import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { authService } from "../services/auth";
import { useAuthStore } from "../store/authStore";
import { CaptchaWidget } from "../components/CaptchaWidget";
import type { CreateUserInput } from "@clawplete/shared/types";
import { UserRole } from "@clawplete/shared/types";

// Test mode bypass for E2E testing
const isTestMode = import.meta.env.VITE_TEST_MODE === "true";

export default function RegisterPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [formData, setFormData] = useState<CreateUserInput>({
    email: "",
    password: "",
    role: UserRole.RESPONDER,
    display_name: "",
  });
  const [error, setError] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(isTestMode ? "test-bypass" : null);
  const [captchaError, setCaptchaError] = useState(false);

  const registerMutation = useMutation({
    mutationFn: ({ formData, captchaToken }: { formData: CreateUserInput; captchaToken: string }) =>
      authService.register(formData, captchaToken),
    onSuccess: (data) => {
      setAuth(data.user, data.tokens);
      navigate("/dashboard");
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || "Registration failed");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Block submission if CAPTCHA not verified
    if (!captchaToken) {
      setCaptchaError(true);
      setError("Please complete the CAPTCHA to continue");
      return;
    }

    // Trim whitespace from inputs to handle copy-paste issues
    const trimmedData = {
      ...formData,
      email: formData.email.trim(),
      password: formData.password.trim(),
      display_name: formData.display_name?.trim(),
    };

    registerMutation.mutate({ formData: trimmedData, captchaToken });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-900 px-4">
      <div className="max-w-md w-full card">
        <div className="text-center mb-8">
          <span className="text-4xl">🦞</span>
          <h1 className="text-3xl font-bold text-primary-400 mt-4">
            Create an Account
          </h1>
          <p className="text-gray-400 mt-2">Join Clawplete today</p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              className="input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              className="input"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
            <p className="mt-1 text-sm text-gray-400">
              Must be at least 8 characters with uppercase, lowercase, and number
            </p>
          </div>

          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-300 mb-2">
              I want to
            </label>
            <select
              id="role"
              className="input"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
            >
              <option value={UserRole.RESPONDER}>Take surveys and earn rewards</option>
              <option value={UserRole.QUESTION_OWNER}>Create surveys</option>
            </select>
          </div>

          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-gray-300 mb-2">
              Display Name (optional)
            </label>
            <input
              id="displayName"
              type="text"
              className="input"
              value={formData.display_name || ""}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
            />
          </div>

          {/* Cloudflare Turnstile CAPTCHA */}
          <div>
            <CaptchaWidget
              onVerify={(token) => {
                setCaptchaToken(token);
                setCaptchaError(false);
              }}
              onError={() => {
                setCaptchaToken(null);
                setCaptchaError(true);
                setError("CAPTCHA validation failed. Please try again.");
              }}
              onExpire={() => {
                setCaptchaToken(null);
              }}
              theme="auto"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary w-full"
            disabled={registerMutation.isPending}
          >
            {registerMutation.isPending ? "Creating account..." : "Register"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-400">
          Already have an account?{" "}
          <Link to="/login" className="text-primary-300 hover:text-primary-200 font-medium">
            Login
          </Link>
        </p>
      </div>
    </div>
  );
}
